// DriftAlarmDrawer.js — Left-side drawer for alarm detail (PRD-48)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAlarmDetail, getFindingDetail, getAlarmRemediation } from '../services/driftService';
import { track as trackTelemetry } from '../services/telemetryService';
import HttpTransactionView from './common/HttpTransactionView';
import AcceptAlarmModal from './AcceptAlarmModal';
import IgnoreAlarmModal from './IgnoreAlarmModal';
import './DriftAlarmDrawer.css';

function timeAgo(iso) {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return iso;
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
}

function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// PRD-129: exploitability tag → human label. Tag values come from the API's
// deriveExploitability (cisa-kev / epss-high|medium|low / no-known-exploit).
const EXPLOIT_LABELS = {
    'cisa-kev': 'CISA KEV · Actively Exploited',
    'epss-high': 'High Exploit Likelihood',
    'epss-medium': 'Moderate Exploit Likelihood',
    'epss-low': 'Low Exploit Likelihood',
    'no-known-exploit': 'No Known Exploit',
};

function DriftAlarmDrawer({ alarmId, assetMap, onClose, onAlarmActioned }) {
    const navigate = useNavigate();
    const [alarm, setAlarm] = useState(null);
    const [finding, setFinding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Remediation
    const [remediation, setRemediation] = useState(null);
    const [remLoading, setRemLoading] = useState(false);
    const [remError, setRemError] = useState(null);

    // Modals
    const [acceptOpen, setAcceptOpen] = useState(false);
    const [ignoreOpen, setIgnoreOpen] = useState(false);

    // Load alarm + finding
    useEffect(() => {
        if (!alarmId) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const alarmData = await getAlarmDetail(alarmId);
                if (cancelled) return;
                setAlarm(alarmData);

                // Load finding detail for HTTP transaction (if vuln alarm)
                if (alarmData.findingId && alarmData.assetId &&
                    alarmData.templateCategory && !alarmData.templateCategory.startsWith('drift-')) {
                    const findingData = await getFindingDetail(alarmData.assetId, alarmData.findingId);
                    if (!cancelled && findingData) setFinding(findingData);
                }

                // Load remediation if cached on alarm
                if (alarmData.remediationText) {
                    setRemediation({
                        remediationText: alarmData.remediationText,
                        remediationGeneratedAt: alarmData.remediationGeneratedAt,
                        cached: true,
                    });
                }
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load alarm');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [alarmId]);

    // Request remediation
    const loadRemediation = useCallback(async (forceRefresh = false) => {
        if (!alarmId) return;
        setRemLoading(true);
        setRemError(null);
        try {
            const result = await getAlarmRemediation(alarmId, forceRefresh);
            setRemediation(result);
        } catch (err) {
            setRemError(err.message || 'Failed to load remediation');
        } finally {
            setRemLoading(false);
        }
    }, [alarmId]);

    // Auto-load remediation if not cached
    useEffect(() => {
        if (alarm && !alarm.remediationText && !remediation && !remLoading) {
            loadRemediation();
        }
    }, [alarm]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleActionSuccess = (result) => {
        onAlarmActioned && onAlarmActioned(result);
    };

    // PRD-151 WP2: alarm disposition telemetry. `disposition` is a fixed enum
    // ('resolve' | 'ignore') — never the alarm's asset/domain value.
    const handleDispositionSuccess = (disposition) => (result) => {
        try {
            trackTelemetry('action', {
                meta: { feature: 'alarm', action: 'disposition', detail: disposition },
            });
        } catch (e) { /* best-effort */ }
        handleActionSuccess(result);
    };

    // PRD-151 WP2: user-initiated remediation request (distinct from the
    // auto-load effect). `wrapped` fires telemetry then defers to loadRemediation.
    const requestRemediationTracked = (forceRefresh = false) => {
        try {
            trackTelemetry('action', {
                meta: { feature: 'alarm', action: 'remediation_request' },
            });
        } catch (e) { /* best-effort */ }
        loadRemediation(forceRefresh);
    };

    const copyAlarmId = () => {
        navigator.clipboard.writeText(alarmId).catch(() => {});
    };

    // Detection timeline
    const detectionHistory = alarm?.detectionHistory ? (() => {
        try { return JSON.parse(alarm.detectionHistory); } catch { return []; }
    })() : [];

    const httpTransaction = finding?.httpTransaction || null;

    // PRD-129: structured KEV/EPSS + liveness context. Prefer the fresh value from
    // the remediation response, fall back to what the alarm detail (formatAlarm)
    // carried. Pure alarm-row data — independent of the HTTP-evidence gate above,
    // so the badge renders for vuln alarms AND EOL drift synthetics with CVEs.
    const enrichment = remediation?.enrichmentContext || alarm?.enrichmentContext || null;
    const hasExploitBadge = !!(enrichment && Array.isArray(enrichment.cves) && enrichment.cves.length > 0);

    return (
        <>
            {/* Backdrop */}
            <div className="da-drawer-backdrop" onClick={onClose} />

            {/* Drawer */}
            <div className="da-drawer">
                {/* Header */}
                <div className="da-drawer__header">
                    <button className="da-drawer__close" onClick={onClose}>×</button>
                    {alarm && (
                        <span className="da-drawer__severity" data-severity={alarm.severity}>
                            Alarm · {(alarm.severity || 'info').toUpperCase()}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="da-drawer__loading">Loading alarm details...</div>
                ) : error ? (
                    <div className="da-drawer__error">{error}</div>
                ) : alarm ? (
                    <div className="da-drawer__content">
                        {/* Title */}
                        <div className="da-drawer__title">
                            <h2>{alarm.name || alarm.templateId || 'Alarm'}</h2>
                            <div className="da-drawer__host">{alarm.matchedAt || '—'}</div>
                        </div>

                        {/* Timing */}
                        <div className="da-drawer__timing">
                            <div className="da-drawer__timing-row">
                                <span className="da-drawer__timing-label">First detected</span>
                                <span>{timeAgo(alarm.firstDetectedAt)} · {formatDate(alarm.firstDetectedAt)}</span>
                            </div>
                            <div className="da-drawer__timing-row">
                                <span className="da-drawer__timing-label">Last detected</span>
                                <span>{timeAgo(alarm.lastDetectedAt)} · {formatDate(alarm.lastDetectedAt)}</span>
                            </div>
                            <div className="da-drawer__timing-row">
                                <span className="da-drawer__timing-label">Detection count</span>
                                <span>{alarm.detectionCount || 1} occurrence{(alarm.detectionCount || 1) > 1 ? 's' : ''}</span>
                            </div>
                        </div>

                        {/* Detection Timeline */}
                        {detectionHistory.length > 0 && (
                            <div className="da-drawer__section">
                                <h3 className="da-drawer__section-title">Detection Timeline</h3>
                                <div className="da-drawer__timeline">
                                    {detectionHistory.map((d, i) => (
                                        <div key={i} className="da-drawer__timeline-item">
                                            <span className="da-drawer__timeline-dot" />
                                            <span>{formatDate(d.detectedAt)}</span>
                                            {d.scanId && <span className="da-drawer__timeline-scan">scan #{d.scanId.slice(0, 8)}</span>}
                                            {i === detectionHistory.length - 1 && <span className="da-drawer__timeline-tag">(first)</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Finding Details */}
                        <div className="da-drawer__section">
                            <h3 className="da-drawer__section-title">Finding Details</h3>
                            {alarm.description && (
                                <div className="da-drawer__detail-row">
                                    <span className="da-drawer__detail-label">Description:</span>
                                    <span className="da-drawer__detail-value">{alarm.description}</span>
                                </div>
                            )}
                            {alarm.templateId && (
                                <div className="da-drawer__detail-row">
                                    <span className="da-drawer__detail-label">Template ID:</span>
                                    <span className="da-drawer__detail-value mono">{alarm.templateId}</span>
                                </div>
                            )}
                            {finding?.cves && finding.cves.length > 0 && (
                                <div className="da-drawer__detail-row">
                                    <span className="da-drawer__detail-label">CVEs:</span>
                                    <span className="da-drawer__detail-value">{finding.cves.join(', ')}</span>
                                </div>
                            )}
                            {finding?.tags && finding.tags.length > 0 && (
                                <div className="da-drawer__tags">
                                    {finding.tags.map((t, i) => <span key={i} className="da-drawer__tag">{t}</span>)}
                                </div>
                            )}
                            {finding?.reference && finding.reference.length > 0 && (
                                <div className="da-drawer__detail-row">
                                    <span className="da-drawer__detail-label">References:</span>
                                    <span className="da-drawer__detail-value">
                                        {finding.reference.map((r, i) => (
                                            <a key={i} href={r} target="_blank" rel="noopener noreferrer" className="da-drawer__ref-link">{r}</a>
                                        ))}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Exploitability & Evidence (PRD-129) — header shows if
                            EITHER the KEV/EPSS badge OR the HTTP transaction is present.
                            The badge renders purely from enrichmentContext (bypasses the
                            !startsWith('drift-') evidence gate); the HTTP transaction keeps
                            that gate (it only populates for non-drift categories). */}
                        {(hasExploitBadge || httpTransaction) && (
                            <div className="da-drawer__section">
                                <h3 className="da-drawer__section-title">Exploitability &amp; Evidence</h3>

                                {hasExploitBadge && (
                                    <div className="da-drawer__exploit">
                                        <div className="da-drawer__exploit-badges">
                                            <span className={`da-exploit-badge da-exploit-badge--${enrichment.exploitability || 'no-known-exploit'}`}>
                                                {EXPLOIT_LABELS[enrichment.exploitability] || enrichment.exploitability || 'No Known Exploit'}
                                            </span>
                                            {enrichment.ransomware && (
                                                <span className="da-exploit-badge da-exploit-badge--ransomware">Ransomware</span>
                                            )}
                                            {typeof enrichment.epssMax === 'number' && (
                                                <span className="da-exploit-epss" title="EPSS: probability of exploitation in the next 30 days">
                                                    EPSS {(enrichment.epssMax * 100).toFixed(1)}%
                                                </span>
                                            )}
                                        </div>

                                        <div className="da-drawer__cve-chips">
                                            {enrichment.cves.map((c, i) => (
                                                <a key={i} className="da-cve-chip"
                                                    href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(c)}`}
                                                    target="_blank" rel="noopener noreferrer">{c}</a>
                                            ))}
                                        </div>

                                        <div className="da-drawer__liveness" data-live={enrichment.findingLive ? 'yes' : 'no'}>
                                            {enrichment.findingLive
                                                ? `Still active${typeof enrichment.evidenceAgeDays === 'number' ? ` · evidence ${enrichment.evidenceAgeDays}d old` : ''}`
                                                : 'No longer active in the latest scan'}
                                        </div>
                                    </div>
                                )}

                                {httpTransaction && (
                                    <HttpTransactionView httpTransaction={httpTransaction} />
                                )}
                            </div>
                        )}

                        {/* AI Remediation */}
                        <div className="da-drawer__section">
                            <div className="da-drawer__section-header-row">
                                <h3 className="da-drawer__section-title">AI Remediation</h3>
                                {remediation && !remLoading && (
                                    <button className="da-drawer__refresh-btn" onClick={() => requestRemediationTracked(true)}>
                                        Refresh
                                    </button>
                                )}
                            </div>
                            {remLoading ? (
                                <div className="da-drawer__rem-loading">Generating remediation guidance...</div>
                            ) : remError ? (
                                <div className="da-drawer__rem-error">{remError}</div>
                            ) : remediation?.remediationText ? (
                                <div className="da-drawer__rem-text">
                                    {remediation.remediationText.split('\n').map((line, i) => (
                                        <p key={i}>{line}</p>
                                    ))}
                                    {remediation.remediationGeneratedAt && (
                                        <div className="da-drawer__rem-meta">
                                            Generated {timeAgo(remediation.remediationGeneratedAt)}
                                            {remediation.cached && ' (cached)'}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="da-drawer__rem-empty">
                                    <button className="da-drawer__rem-trigger" onClick={() => requestRemediationTracked()}>
                                        Generate AI Remediation
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                {/* Actions */}
                {alarm && alarm.status === 'open' && (
                    <div className="da-drawer__actions">
                        <div className="da-drawer__action-buttons">
                            <button
                                className="da-drawer__btn da-drawer__btn--accept"
                                onClick={() => setAcceptOpen(true)}
                                title="Acceptable risk. This finding will stay in your risk score, but the alarm will not fire again until the suppression expires."
                            >
                                <span className="da-drawer__btn-label">Accept</span>
                                <span className="da-drawer__btn-sub">This is a real risk I know about</span>
                            </button>
                            <button
                                className="da-drawer__btn da-drawer__btn--ignore"
                                onClick={() => setIgnoreOpen(true)}
                                title="False positive. This finding will be removed from your risk score and hidden from alarms until the suppression expires."
                            >
                                <span className="da-drawer__btn-label">Ignore</span>
                                <span className="da-drawer__btn-sub">False positive</span>
                            </button>
                        </div>
                        <div className="da-drawer__secondary-actions">
                            {alarm.assetId && (
                                <button className="da-drawer__link" onClick={() => navigate(`/assets/${alarm.assetId}`)}>
                                    Go to Asset →
                                </button>
                            )}
                            <button className="da-drawer__link" onClick={copyAlarmId}>
                                Copy alarm ID
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <AcceptAlarmModal
                isOpen={acceptOpen}
                onClose={() => setAcceptOpen(false)}
                alarm={alarm}
                assetId={alarm?.assetId}
                onSuccess={handleDispositionSuccess('resolve')}
            />
            <IgnoreAlarmModal
                isOpen={ignoreOpen}
                onClose={() => setIgnoreOpen(false)}
                alarm={alarm}
                assetId={alarm?.assetId}
                onSuccess={handleDispositionSuccess('ignore')}
            />
        </>
    );
}

export default DriftAlarmDrawer;
